import type { LucideIcon } from 'lucide-react'

import {
    Award,
    Building2,
    CalendarDays,
    Gem,
    GraduationCap,
    HeartHandshake,
    Landmark,
    Layers,
    Medal,
    PenTool,
    Quote,
    Ruler,
    School,
    Sparkles,
    Stamp,
    Trophy,
    Type,
    Users
} from 'lucide-react'

export type IconItem = {
    icon: LucideIcon
    title: string
    description: string
}

export type ServiceItem = IconItem & {
    id: string
    includes: Array<string>
}

export type StepItem = {
    number: string
    title: string
    description: string
}

export type FaqItem = {
    question: string
    answer: string
}

export const heroHighlights: Array<string> = [
    'Diseño personalizado',
    'Grabado de precisión',
    'Materiales seleccionados'
]

export const customizationOptions: Array<IconItem> = [
    {
        icon: Type,
        title: 'Grabado de nombres',
        description: 'Nombres de galardonados, equipos o promociones con tipografías legibles y proporcionadas.'
    },
    {
        icon: Quote,
        title: 'Inscripciones y dedicatorias',
        description: 'Mensajes de agradecimiento, frases y menciones redactadas para la ocasión.'
    },
    {
        icon: CalendarDays,
        title: 'Fechas y mensajes',
        description: 'Fechas de eventos, aniversarios e inauguraciones integradas en el diseño.'
    },
    {
        icon: Stamp,
        title: 'Logotipos institucionales',
        description: 'Escudos, emblemas y logotipos reproducidos a partir de tus archivos.'
    },
    {
        icon: Layers,
        title: 'Materiales',
        description: 'Latón, acero, bronce, cristal o madera según el carácter de la pieza.'
    },
    {
        icon: Sparkles,
        title: 'Acabados',
        description: 'Cepillado, pulido, satinado o envejecido para lograr el efecto deseado.'
    },
    {
        icon: Ruler,
        title: 'Dimensiones',
        description: 'Formatos adaptados al espacio de exhibición y a la escala del evento.'
    },
    {
        icon: PenTool,
        title: 'Diseños especiales',
        description: 'Piezas desarrolladas a medida cuando el reconocimiento necesita una forma propia.'
    }
]

export const services: Array<ServiceItem> = [
    {
        id: 'diseno-personalizado',
        icon: PenTool,
        title: 'Diseño personalizado',
        description: 'Proponemos una pieza a partir de la ocasión, la identidad visual de tu organización y el espacio donde se exhibirá.',
        includes: [
            'Propuesta visual previa',
            'Adaptación de logotipos y escudos',
            'Ajustes antes de la aprobación'
        ]
    },
    {
        id: 'grabado-e-inscripcion',
        icon: Type,
        title: 'Grabado e inscripción',
        description: 'Textos, fechas y emblemas grabados sobre metal, cristal o madera, cuidando la legibilidad y la composición de cada línea.',
        includes: [
            'Técnica de grabado según el material',
            'Revisión del texto antes de producir',
            'Composición tipográfica equilibrada'
        ]
    },
    {
        id: 'personalizacion-de-reconocimientos',
        icon: Award,
        title: 'Personalización de reconocimientos',
        description: 'Adaptamos piezas del catálogo con nombres, menciones, acabados y presentaciones para cada galardonado.',
        includes: [
            'Personalización individual por pieza',
            'Opciones de acabado y presentación',
            'Estuches y empaques de presentación'
        ]
    },
    {
        id: 'placas-conmemorativas',
        icon: Landmark,
        title: 'Elaboración de placas conmemorativas',
        description: 'Placas para inauguraciones, homenajes y aniversarios, con montaje de sobremesa o para pared.',
        includes: [
            'Formatos para interior y exterior',
            'Separadores y anclajes de montaje',
            'Bases de madera o piedra'
        ]
    },
    {
        id: 'trofeos-y-medallas',
        icon: Trophy,
        title: 'Producción de trofeos y medallas',
        description: 'Coordinamos la producción de trofeos y medallas para premiaciones deportivas, académicas y corporativas.',
        includes: [
            'Series para primeros lugares',
            'Cintas en colores institucionales',
            'Grabado individual de cada pieza'
        ]
    },
    {
        id: 'piezas-especiales',
        icon: Gem,
        title: 'Piezas especiales bajo pedido',
        description: 'Desarrollamos galardones de autor y condecoraciones cuando el premio necesita una forma propia.',
        includes: [
            'Diseño escultórico a medida',
            'Combinación de materiales',
            'Acompañamiento en cada etapa'
        ]
    }
]

export const quoteSteps: Array<StepItem> = [
    {
        number: '01',
        title: 'Cuéntanos tu idea',
        description: 'Indícanos qué deseas reconocer, la ocasión y el tipo de pieza que imaginas.'
    },
    {
        number: '02',
        title: 'Comparte tus especificaciones',
        description: 'Cantidades, materiales, textos, logotipos y la fecha en la que necesitas las piezas.'
    },
    {
        number: '03',
        title: 'Recibe una propuesta',
        description: 'Preparamos una propuesta con alternativas de diseño, acabados y condiciones.'
    },
    {
        number: '04',
        title: 'Confirma los detalles de tu pedido',
        description: 'Revisas la propuesta, ajustamos juntos los detalles y avanzamos con tu pedido.'
    }
]

export const serviceFaqs: Array<FaqItem> = [
    {
        question: '¿Puedo cotizar una sola pieza?',
        answer: 'Puedes solicitar una cotización tanto para una pieza individual como para series completas. La viabilidad y las condiciones de cada pedido se confirman en la propuesta.'
    },
    {
        question: '¿Qué archivos necesito para incluir un logotipo?',
        answer: 'Lo ideal es un PDF vectorial. Si solo tienes una imagen, compártela en buena resolución en formato JPG o PNG y revisaremos si es apta para el grabado.'
    },
    {
        question: '¿Cuánto tiempo toma la elaboración?',
        answer: 'Depende del tipo de pieza, la cantidad y el grado de personalización. Indícanos la fecha en la que necesitas tus piezas y los plazos se confirmarán en la propuesta.'
    },
    {
        question: '¿Los productos del catálogo tienen precios fijos?',
        answer: 'El catálogo es orientativo y no muestra precios. Cada pieza se cotiza según materiales, dimensiones, cantidad y nivel de personalización.'
    },
    {
        question: '¿Puedo revisar el diseño antes de confirmar?',
        answer: 'La propuesta describe la pieza y, cuando corresponde, incluye una referencia visual para que puedas revisarla y solicitar ajustes antes de confirmar.'
    }
]

export const clientTypes: Array<IconItem> = [
    {
        icon: Building2,
        title: 'Empresas privadas',
        description: 'Premiaciones internas, aniversarios y reconocimientos al desempeño.'
    },
    {
        icon: Landmark,
        title: 'Instituciones públicas',
        description: 'Distinciones protocolares, condecoraciones y placas oficiales.'
    },
    {
        icon: School,
        title: 'Centros educativos',
        description: 'Medallas, diplomas de honor y reconocimientos estudiantiles.'
    },
    {
        icon: GraduationCap,
        title: 'Universidades',
        description: 'Graduaciones, investigación y trayectorias académicas.'
    },
    {
        icon: Medal,
        title: 'Organizaciones deportivas',
        description: 'Campeonatos, torneos y competencias de todas las disciplinas.'
    },
    {
        icon: CalendarDays,
        title: 'Organizadores de eventos',
        description: 'Galas, congresos y ceremonias que requieren piezas a la altura.'
    },
    {
        icon: Users,
        title: 'Asociaciones',
        description: 'Gremios, clubes y colectivos que celebran a sus miembros.'
    },
    {
        icon: HeartHandshake,
        title: 'Clientes particulares',
        description: 'Homenajes, agradecimientos y fechas personales significativas.'
    }
]

export const aboutPillars: Array<IconItem> = [
    {
        icon: Award,
        title: 'Filosofía de marca',
        description: 'Entendemos el reconocimiento como un gesto que trasciende el evento. Cada pieza debe conservar el valor del logro y la intención de quien la entrega.'
    },
    {
        icon: PenTool,
        title: 'Enfoque de personalización',
        description: 'Partimos de la ocasión, del espacio y de la identidad de cada organización para proponer formas, textos, materiales y acabados con sentido.'
    },
    {
        icon: Gem,
        title: 'Compromiso con la calidad',
        description: 'Cuidamos la selección de materiales, la legibilidad de cada grabado y la presentación final, porque los detalles son los que se recuerdan.'
    }
]

export const presupuestoOptions: Array<string> = [
    'Hasta USD 100',
    'USD 100 a 500',
    'USD 500 a 1.500',
    'USD 1.500 a 5.000',
    'Más de USD 5.000',
    'Prefiero no indicarlo'
]

export const asuntoOptions: Array<string> = [
    'Información general',
    'Productos y personalización',
    'Alianzas y proveedores',
    'Otro'
]
